import PropTypes from 'prop-types';
import cn from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { REVIEW_STEP } from 'constants/checkoutFlow';
import { evPlaceOrder } from 'events/checkout';

import { onEvent, supportsPassiveEventListener } from 'helpers/EventHelpers';
import { trackEvent } from 'helpers/analytics';
import { pushPlaceOrder, setShouldDefaultOptions } from 'actions/checkout/purchase';
import { track } from 'apis/amethyst';
import ChargeSummary from 'components/checkout/caap/spc/ChargeSummary';
import DesktopOnly from 'components/checkout/caap/DesktopOnly';
import Imb from 'components/checkout/caap/spc/Imb';
import OrderSummaryShipSection from 'components/checkout/caap/spc/OrderSummaryShipSection';
import PlaceOrderButtonSection from 'components/checkout/caap/spc/PlaceOrderButtonSection';
import SmallScreenOnly from 'components/checkout/caap/SmallScreenOnly';
import StepSection from 'components/checkout/caap/StepSection';
import { isReadyToSubmit } from 'helpers/CheckoutUtils';
import css from 'styles/containers/checkout/chargeSummarySection.scss';
import { getOffset } from 'utils';

export class ChargeSummarySection extends Component {
  componentDidMount() {
    const opts = supportsPassiveEventListener() ? { passive: true } : false;
    onEvent(window, 'scroll', this.onScroll, opts, this);
    onEvent(window, 'resize', this.onScroll, opts, this);
    this.onScroll();
  }

  componentDidUpdate(prevProps) {
    const { spcRef: prevSpcRef } = prevProps;
    const { spcRef } = this.props;
    if (spcRef !== prevSpcRef) {
      this.spcRef = spcRef;
    }
  }

  onPlaceOrderClicked = () => {
    const { checkoutData, pushPlaceOrder } = this.props;
    trackEvent('TE_CHECKOUT_SPC_PLACE_ORDER', 'top');
    track(evPlaceOrder, { ...checkoutData });
    pushPlaceOrder();
  }

  onScroll = () => {
    if (!this.summarySectionRef || !this.spcRef) {
      return;
    }

    const { position } = this.state || {};
    const scrollTop = window.pageYOffset;
    const panelBoundingRect = this.spcRef.getBoundingClientRect();
    const topBoundry = getOffset(this.spcRef).top;
    const summarySectionBoundingRect = this.summarySectionRef.getBoundingClientRect();
    const bufferFromTop = 14; // we dont' want subtotals flush to the screen edge
    const summarySectionHeight = summarySectionBoundingRect.bottom - summarySectionBoundingRect.top;
    const bottomLeftCornerOfPanelContainer = topBoundry + panelBoundingRect.bottom - panelBoundingRect.top;
    const scrollTopCap = bottomLeftCornerOfPanelContainer - summarySectionHeight - bufferFromTop;
    const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const checkoutSectionWidth = 1000;

    if (scrollTop > scrollTopCap) {
      let absoluteTop = scrollTopCap - topBoundry + bufferFromTop;
      absoluteTop = absoluteTop < 0 ? 0 : absoluteTop + 10;
      this.summarySectionRef.setAttribute('style', `top: ${absoluteTop}px;`);
      if (position !== 'absolute') {
        this.setState({ position: 'absolute' });
      }
    } else {
      if (scrollTop >= topBoundry) {
        if (windowWidth <= checkoutSectionWidth) {
          const relativeTop = scrollTop - topBoundry + bufferFromTop;
          this.summarySectionRef.setAttribute('style', `top: ${relativeTop}px;`);
          if (position !== 'relative') {
            this.setState({ position: 'relative' });
          }
        } else {
          if (position !== 'fixed') {
            this.summarySectionRef.setAttribute('style', `top: ${bufferFromTop}px;`);
            this.setState({ position: 'fixed' });
          }
        }
      } else {
        if (position !== 'relativeFixedTop') {
          this.summarySectionRef.setAttribute('style', 'top: 0;');
          this.setState({ position: 'relativeFixedTop' });
        }
      }
    }
  }

  onToggleShouldDefaultOptionsClick = () => {
    const {
      checkoutData: { purchaseData: { shouldDefaultOptions } },
      setShouldDefaultOptions
    } = this.props;
    const newState = !shouldDefaultOptions;
    trackEvent('TE_CHECKOUT_SPC_TOGGLE_DEFAULTS', `${newState}`);
    setShouldDefaultOptions(newState);
  }

  showChargeSummary() {
    const { shipOptions } = this.props.checkoutData;
    return this.showReviewStep() && !shipOptions.showModal;
  }

  showReviewStep() {
    const { purchaseData: { targetStep } } = this.props.checkoutData;
    return targetStep === REVIEW_STEP;
  }

  render() {
    const {
      shipOptions,
      purchaseData: {
        cartType,
        data: {
          chargeSummary = {},
          deliveryPromise,
          shippingAddress
        },
        isFetchingCheckoutData,
        isOrderBeingPlaced,
        numItems,
        purchaseGiftCard,
        shouldDefaultOptions,
        showShouldDefaultOptions
      }
    } = this.props.checkoutData;

    const { position } = this.state || {};

    const { testId } = this.context;

    const stepClass = cn(css.wrapper, {
      [css.fixed]: position === 'fixed',
      [css.absolute]: position === 'absolute',
      [css.relative]: position === 'relative' || position === 'relativeFixedTop'
    });

    const isOrderReadyToSubmit = !isFetchingCheckoutData && isReadyToSubmit(this.props.checkoutData);
    const showImb = showShouldDefaultOptions && this.showReviewStep() && !shipOptions.showModal;

    return (
      <div ref={el => this.summarySectionRef = el} className={stepClass}>
        <StepSection stepClass={css.chargeSummarySection} isCurrentStep={this.showChargeSummary()} dataTestIdLoader={`${testId('spinner')}`}>
          <div>
            <PlaceOrderButtonSection
              isOrderBeingPlaced={isOrderBeingPlaced}
              isReadyToSubmit={isOrderReadyToSubmit}
              isChargeSummary={true}
              onPlaceOrderClicked={this.onPlaceOrderClicked} />

            <SmallScreenOnly>
              <Imb
                headerText="Important message"
                onToggleShouldDefaultOptionsClick={this.onToggleShouldDefaultOptionsClick}
                shouldDefaultOptions={shouldDefaultOptions}
                showImb={showImb} />
            </SmallScreenOnly>

            <DesktopOnly>
              <hr />
            </DesktopOnly>

            <h2>Order Summary</h2>

            <OrderSummaryShipSection
              shippingAddress={shippingAddress}
              cartType={cartType}
              deliveryPromise={deliveryPromise} />

            <ChargeSummary
              cartType={cartType}
              chargeSummary={chargeSummary}
              numItems={numItems}
              purchaseGiftCard={purchaseGiftCard} />
          </div>
        </StepSection>
      </div>
    );
  }
}

ChargeSummarySection.contextTypes = {
  testId: PropTypes.func
};

function mapStateToProps(state) {
  return {
    checkoutData: state.checkoutData
  };
}

export default connect(mapStateToProps, {
  pushPlaceOrder,
  setShouldDefaultOptions
})(ChargeSummarySection);
